import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';

const CREDITS_KEY = 'credits';

// =========================================
// Queries
// =========================================

/** Fetch current user credit balance */
export function useCredits() {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [CREDITS_KEY, user?.id],
    queryFn: async (): Promise<number> => {
      if (!user) return 0;

      const { data, error } = await supabase
        .from('profiles')
        .select('credits')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data?.credits ?? 0;
    },
    enabled: !!user,
    refetchInterval: 30000, // Refresh every 30s
  });
}

/** Check if user has enough credits */
export function useHasCredits(amount: number = 1) {
  const { data: credits, isLoading } = useCredits();
  return {
    hasCredits: (credits ?? 0) >= amount,
    credits: credits ?? 0,
    isLoading,
  };
}

/** Hook to invalidate credits after mutations */
export function useInvalidateCredits() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return () => {
    if (user) {
      queryClient.invalidateQueries({ queryKey: [CREDITS_KEY, user.id] });
    }
  };
}
